/**
 * <ai-product-detail> coordinates PDP business state.
 *
 * Layout and visual styling stay in Liquid/CSS. This component owns variant
 * synchronization across price, inventory, purchase controls, media, forms,
 * cart quantity rules, pickup availability, share URL, and downstream events.
 */
if (!customElements.get("ai-product-detail")) {
  customElements.define(
    "ai-product-detail",
    class ProductInfo extends HTMLElement {
      connectedCallback() {
        this._variants = this._readJSON("[data-product-variants]", []);
        this._options = this._readJSON("[data-product-options]", []);
        this._media = this._readJSON("[data-product-media]", []);
        this._product = this._readJSON("[data-product]", {});
        this._currentVariant = this._resolveInitialVariant();

        this._boundOnVariantChange ||= this._onVariantChange.bind(this);
        this._boundOnQuantityChange ||= this._onQuantityChange.bind(this);
        this._boundOnQuickAddError ||= this._onQuickAddError.bind(this);
        this._boundOnCartAdd ||= () => this._syncCartState(this._currentVariant);
        this._boundStickyClick ||= this._onStickyClick.bind(this);

        this.addEventListener("variant:change", this._boundOnVariantChange);
        this.addEventListener("quantity:change", this._boundOnQuantityChange);
        this.addEventListener("qac:add-error", this._boundOnQuickAddError);
        this.addEventListener("product-form:add-success", this._boundOnCartAdd);
        this.addEventListener("click", this._boundStickyClick);

        if (this._currentVariant) {
          this._updateQuantityRules(this._currentVariant);
          this._updatePurchaseControls(this._currentVariant);
          this._updateStickyCartInfo(this._currentVariant);
          this._syncCartState(this._currentVariant);
          this._updateVariantInputs(this._currentVariant);
          this._updateInstallmentVariantInputs(this._currentVariant);
        }
      }

      disconnectedCallback() {
        this.removeEventListener("variant:change", this._boundOnVariantChange);
        this.removeEventListener("quantity:change", this._boundOnQuantityChange);
        this.removeEventListener("qac:add-error", this._boundOnQuickAddError);
        this.removeEventListener("product-form:add-success", this._boundOnCartAdd);
        this.removeEventListener("click", this._boundStickyClick);
        this._cartRequest = (this._cartRequest || 0) + 1;
        this.abortController?.abort();
      }

      _onVariantChange(event) {
        this._syncVariantSelectorsFromEvent(event);
        this._resetProductFormState();
        const variant = this._resolveVariant(event.detail);
        if (!variant) {
          this._setUnavailable();
          return;
        }

        this._currentVariant = variant;
        this._hideUnavailableBusinessSurfaces(false);
        this._updatePrice(variant);
        this._updateBadge(variant);
        this._updateSku(variant);
        this._updateVolumePricing(variant);
        this._updateInventory(variant);
        this._updateQuantityRules(variant);
        this._updatePurchaseControls(variant);
        this._syncCartState(variant);
        this._updateVariantInputs(variant);
        this._updateInstallmentVariantInputs(variant);
        this._updateMedia(variant);
        this._updatePickupAvailability(variant);
        this._updateProductDiscounts(variant);
        this._updateStickyCartInfo(variant);
        this._updateProductDataJSON(variant);
        this._calcProductPrice(variant);
        this._updateUrl(variant);
        this._publishVariantChange(variant);
        this._announceVariant(variant);
      }

      _onQuantityChange(event) {
        const quantity = Number(event.detail?.quantity || event.target?.getAttribute("value") || 1);
        this.querySelectorAll("[data-product-quantity-input]").forEach((input) => {
          input.value = String(Math.max(1, quantity));
        });
        this._updateProductDiscounts(this._currentVariant);
        this._calcProductPrice(this._currentVariant);
      }

      _onQuickAddError(event) {
        this._showFormError(event.detail?.error || event.detail?.message || "");
      }

      _resolveInitialVariant() {
        const selectedId =
          this.querySelector("ai-variant-selector")?.getAttribute("selected-variant-id") ||
          this.querySelector("[data-product-variant-input]")?.value;
        return this._variants.find((variant) => String(variant.id) === String(selectedId)) ||
          this._variants.find((variant) => variant.available) ||
          this._variants[0] ||
          null;
      }

      _resolveVariant(detail = {}) {
        if (detail.variant) return detail.variant;
        const id = detail.variantId || detail.id || detail.value;
        if (id == null) return null;
        return this._variants.find((variant) => String(variant.id) === String(id)) || null;
      }

      _syncVariantSelectorsFromEvent(event) {
        const selectedOptionValueIds = event.detail?.selectedOptionValueIds;
        if (!Array.isArray(selectedOptionValueIds)) return;

        this.querySelectorAll("ai-variant-selector").forEach((selector) => {
          if (selector === event.target) return;
          selector.applyOptionValueIds?.(selectedOptionValueIds);
        });
      }

      _updatePrice(variant) {
        const priceEl = this.querySelector("[data-product-price]");
        const compareEl = this.querySelector("[data-product-compare-price]");
        const priceWrapper = this.querySelector('[data-product-block="price"]');
        if (priceWrapper) priceWrapper.hidden = false;

        if (priceEl) {
          priceEl.textContent = this._replaceMoneyAmount(priceEl.textContent.trim(), variant.price);
        }

        const hasCompare = variant.compare_at_price != null && variant.compare_at_price > variant.price;
        priceWrapper?.classList.toggle("price--on-sale", hasCompare);
        priceWrapper?.classList.toggle("price--sold-out", !variant.available);

        if (!compareEl) return;
        if (hasCompare) {
          compareEl.textContent = this._replaceMoneyAmount(
            compareEl.textContent.trim() || priceEl?.textContent?.trim() || "",
            variant.compare_at_price,
          );
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      _updateBadge(variant) {
        const badgeEl = this.querySelector("[data-product-badge]");
        if (!badgeEl) return;

        const hasDiscount = variant.compare_at_price != null && variant.compare_at_price > variant.price;
        if (!hasDiscount) {
          badgeEl.hidden = true;
          badgeEl.textContent = "";
          return;
        }

        const discount = variant.compare_at_price - variant.price;
        const percent = Math.ceil((discount / variant.compare_at_price) * 100);
        const format = badgeEl.getAttribute("data-badge-format") || "percentage";
        badgeEl.textContent = format === "percentage"
          ? `-${percent}%`
          : this._replaceMoneyAmount(
            badgeEl.textContent.trim() || this.querySelector("[data-product-price]")?.textContent?.trim() || "",
            discount,
          );
        badgeEl.hidden = false;
      }

      _updateSku(variant) {
        this.querySelectorAll("[data-product-sku]").forEach((skuEl) => {
          skuEl.textContent = variant?.sku || "";
          skuEl.hidden = !variant?.sku;
          if (skuEl.closest("[data-product-sku]")) {
            skuEl.closest("[data-product-sku]").hidden = !variant?.sku;
          }
        });
      }

      _updateInventory(variant) {
        const inventory = this.querySelector("[data-inventory-content]");
        if (!inventory) return;
        inventory.hidden = false;

        // Shopify storefront JSON omits inventory_quantity; only let a stock
        // count influence the badge when the data actually carries one.
        // Otherwise `available` is the single source of truth.
        const hasQuantity = variant.inventory_quantity != null && Number.isFinite(Number(variant.inventory_quantity));
        const quantity = hasQuantity ? Number(variant.inventory_quantity) : 0;
        const threshold = Number(inventory.dataset.inventoryThreshold || 0);
        const policy = variant.inventory_policy || "deny";
        let className = "in-stock";
        let text = inventory.dataset.inStockText || "In stock";

        if (!variant.available || (hasQuantity && quantity <= 0 && policy !== "continue")) {
          className = "out-of-stock";
          text = inventory.dataset.outOfStockText || "Out of stock";
        } else if (hasQuantity && quantity > 0 && quantity <= threshold) {
          className = "low-stock";
          text = inventory.dataset.lowStockText || "Low stock";
        }

        inventory.classList.remove("in-stock", "low-stock", "out-of-stock");
        inventory.classList.add(className);
        const status = inventory.querySelector("[data-inventory-status-text]");
        if (status) status.textContent = text;

        const quantityEl = inventory.querySelector("[data-inventory-quantity]");
        if (quantityEl) {
          quantityEl.textContent = quantity > 0 ? String(quantity) : "";
        }
      }

      _updatePurchaseControls(variant) {
        const disabled = !variant?.available || this._isQuantityRuleSoldOut(variant);
        const text = !variant
          ? this._getUnavailableText()
          : disabled
            ? this._getSoldOutText()
            : this._getAddText();

        this.querySelectorAll("[data-product-variant-input]").forEach((input) => {
          if (input.disabled === disabled) return;
          input.disabled = disabled;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
        this._updateExpressPay(variant);

        this.querySelectorAll("[data-add-to-cart], [slot='add-btn']").forEach((button) => {
          button.disabled = disabled;
          button.setAttribute("aria-disabled", disabled ? "true" : "false");
          button.classList.toggle("disabled", disabled);
          const label = button.querySelector("[data-add-to-cart-label]");
          if (label) label.textContent = text;
        });

        this.querySelectorAll("ai-quick-add-cart").forEach((quickAdd) => {
          if (variant?.id) quickAdd.setAttribute("variant-id", String(variant.id));
          quickAdd.toggleAttribute("aria-disabled", disabled);
        });

        this.querySelectorAll("[data-sticky-cart], .product-sticky-cart").forEach((sticky) => {
          if (variant?.id) sticky.setAttribute("data-variant-id", String(variant.id));
          const priceEl = sticky.querySelector("[data-sticky-cart-price]");
          if (priceEl) priceEl.textContent = this.querySelector("[data-product-price]")?.textContent?.trim() || "";
          sticky.querySelectorAll("[data-sticky-cart-add], [data-sticky-cart-open].sticky-cart-btn--add").forEach((btn) => {
            btn.disabled = disabled;
            btn.textContent = text;
          });
        });
      }

      _updateQuantityRules(variant) {
        const rule = variant?.quantity_rule || {};
        this.querySelectorAll("ai-quantity-input[data-quantity-rules]").forEach((input) => {
          const min = Number(rule.min ?? 1) || 1;
          const step = Number(rule.increment ?? 1) || 1;
          input.setAttribute("min", String(min));
          input.setAttribute("step", String(step));
          if (rule.max == null) {
            input.removeAttribute("max");
          } else {
            input.setAttribute("max", String(rule.max));
          }
          if (Number(input.getAttribute("value") || 0) < min) {
            input.setAttribute("value", String(min));
          }
          input.setAttribute("data-cart-quantity", "0");
          this._applyQuantityBoundaries(input, variant);
        });
      }

      async _syncCartState(variant) {
        const input = this.querySelector("ai-quantity-input[data-quantity-rules]");
        const request = this._cartRequest = (this._cartRequest || 0) + 1;

        try {
          const response = await fetch(`${window.routes?.cart_url || "/cart"}.js`, {
            headers: { Accept: "application/json" },
          });
          if (!response.ok) return;
          if (!this.isConnected || request !== this._cartRequest) return;
          const cart = await response.json();
          if (!this.isConnected || request !== this._cartRequest) return;
          if (Number.isSafeInteger(cart.item_count) && cart.item_count >= 0) {
            this.ownerDocument.querySelectorAll('[data-header-action="cart"] [data-cart-count-bubble]')
              .forEach((count) => { count.textContent = String(cart.item_count); });
          }
          if (!variant?.id || !input || this._currentVariant?.id !== variant.id) return;
          const cartQuantity = (cart.items || [])
            .filter((item) => String(item.variant_id) === String(variant.id))
            .reduce((total, item) => total + Number(item.quantity || 0), 0);
          input.setAttribute("data-cart-quantity", String(cartQuantity));
          this._applyQuantityBoundaries(input, variant);
          this._updatePurchaseControls(variant);
        } catch {
          if (!this.isConnected || request !== this._cartRequest) return;
          if (!variant?.id || !input || this._currentVariant?.id !== variant.id) return;
          // Unknown cart state must not leave the PDP stuck in a sold-out
          // state derived from a stale cart quantity — fail open.
          input.setAttribute("data-cart-quantity", "0");
          this._applyQuantityBoundaries(input, variant);
          this._updatePurchaseControls(variant);
        }
      }

      _applyQuantityBoundaries(input, variant) {
        const rule = variant?.quantity_rule || {};
        const min = Number(rule.min ?? input.getAttribute("min") ?? 1) || 1;
        const step = Number(rule.increment ?? input.getAttribute("step") ?? 1) || 1;
        const cartQuantity = Math.max(0, Number(input.getAttribute("data-cart-quantity")) || 0);

        const ruleMax = Number(rule.max);
        let max = Number.isFinite(ruleMax) && ruleMax > 0 ? ruleMax - cartQuantity : null;

        // Clamp by inventory only when the data affirmatively exposes a
        // positive stock count. Shopify storefront JSON omits
        // inventory_quantity entirely, and Number(undefined || 0) === 0 used
        // to collapse the max to 0 — disabling purchase for every tracked,
        // in-stock variant. Missing inventory data must never cap quantity.
        const inventoryQuantity = Number(variant?.inventory_quantity);
        if (
          variant?.inventory_management &&
          variant.inventory_policy !== "continue" &&
          variant.inventory_quantity != null &&
          Number.isFinite(inventoryQuantity) &&
          inventoryQuantity > 0
        ) {
          const inventoryMax = inventoryQuantity - cartQuantity;
          max = max == null ? inventoryMax : Math.min(max, inventoryMax);
        }

        const nextMin = cartQuantity >= min ? Math.min(min, step) : min;
        input.setAttribute("min", String(Math.max(1, nextMin)));
        if (max == null || max >= 999999) input.removeAttribute("max");
        else input.setAttribute("max", String(Math.max(0, max)));
      }

      _isQuantityRuleSoldOut(variant) {
        const quantity = this.querySelector("ai-quantity-input[data-quantity-rules]");
        const maximum = quantity?.getAttribute("max");
        if (maximum != null && Number(maximum) < Number(quantity.getAttribute("min") || 1)) return true;
        // Boundaries above use explicit quantity rules and tracked inventory.
        // Missing inventory data does not impose a maximum.
        const ruleMax = Number(variant?.quantity_rule?.max);
        if (!Number.isFinite(ruleMax) || ruleMax <= 0) return false;

        const ruleMin = Number(variant?.quantity_rule?.min);
        const min = Number.isFinite(ruleMin) && ruleMin > 0 ? ruleMin : 1;
        const cartQuantity = Number(
          this.querySelector("ai-quantity-input[data-quantity-rules]")?.getAttribute("data-cart-quantity"),
        );
        const inCart = Number.isFinite(cartQuantity) && cartQuantity > 0 ? cartQuantity : 0;
        return ruleMax - inCart < min;
      }

      _updateVariantInputs(variant) {
        this.querySelectorAll("[data-product-variant-input]").forEach((input) => {
          input.value = variant?.id || "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      _updateInstallmentVariantInputs(variant) {
        this.querySelectorAll("[data-product-installment-variant-input]").forEach((input) => {
          input.value = variant?.id || "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      _updateExpressPay(variant) {
        const disabled = !variant?.available || this._isQuantityRuleSoldOut(variant);
        const buyNowContainer = this.querySelector("[data-product-payment-button]");
        if (buyNowContainer) {
          buyNowContainer.hidden = disabled;
          buyNowContainer.style.display = disabled ? "none" : "";
        }
        setTimeout(() => {
          // Re-evaluate at fire time: the variant may have changed while the
          // express-pay scripts were booting.
          const current = this._currentVariant;
          const nowDisabled = !current?.available || this._isQuantityRuleSoldOut(current);
          // Dawn parity: never touch Shopify's accelerated-checkout /
          // payment-button internals — portable-wallets owns their lifecycle,
          // and an externally toggled `disabled` either kills a live button
          // or surfaces a dead one. Sold-out visibility is handled entirely
          // by hiding the semantic container above. Only Genstore's own express
          // button is managed here, marker-scoped so we never strip a
          // disabled state someone else set.
          this.querySelectorAll(".express-buy-it-now").forEach((button) => {
            if (nowDisabled) {
              button.setAttribute("disabled", "disabled");
              button.setAttribute("data-ai-express-disabled", "");
            } else if (button.hasAttribute("data-ai-express-disabled")) {
              button.removeAttribute("disabled");
              button.removeAttribute("data-ai-express-disabled");
            }
          });
        }, 500);
        window.pExpressPay?.handleUpdateSkuId?.(variant?.id);
      }

      _updateMedia(variant) {
        const mediaId = this._getVariantMediaId(variant);
        if (mediaId != null) {
          this.querySelector("ai-media-gallery")?.setActiveMedia?.(mediaId);
        }

        const src = this._getVariantImageSrc(variant);
        const imgEl = this.querySelector("[data-product-featured-image]") ||
          this.querySelector(".mg-slide--active img[data-media-image]");
        if (src && imgEl && imgEl.getAttribute("src") !== src) {
          imgEl.setAttribute("src", src);
        }
        this._syncMediaModalAndXR(variant);
      }

      _updatePickupAvailability(variant) {
        this.querySelectorAll("[data-pickup-availability], ai-pickup-availability, pickup-availability").forEach((pickup) => {
          if (!variant?.available) {
            pickup.hidden = true;
            pickup.removeAttribute("available");
            pickup.innerHTML = "";
            return;
          }
          pickup.hidden = false;
          pickup.setAttribute("variant-id", String(variant.id));
          pickup.update?.(variant);
        });
      }

      _updateProductDiscounts(variant) {
        this.querySelectorAll("ai-product-discounts, [data-product-discounts]").forEach((discounts) => {
          discounts.update?.({
            variant,
            quantity: Number(this.querySelector("[data-product-quantity-input]")?.value || 1),
          });
          discounts.dispatchEvent?.(
            new CustomEvent("product-discounts:update", {
              bubbles: true,
              detail: { variant },
            }),
          );
        });
      }

      _updateVolumePricing(variant) {
        const unitPrice = variant?.unit_price;
        const measurement = variant?.unit_price_measurement;
        const pricePerItem = this.querySelector("[data-product-price-per-item]");
        const volume = this.querySelector("[data-product-volume-pricing]");
        const note = this.querySelector("[data-product-volume-note]");

        if (pricePerItem) {
          if (unitPrice != null && measurement) {
            pricePerItem.hidden = false;
            pricePerItem.textContent = `${this._replaceMoneyAmount(pricePerItem.textContent || this.querySelector("[data-product-price]")?.textContent || "", unitPrice)} / ${measurement.reference_value || 1}${measurement.reference_unit || ""}`;
          } else {
            pricePerItem.hidden = true;
            pricePerItem.textContent = "";
          }
        }
        if (volume) volume.hidden = !variant?.quantity_price_breaks?.length;
        if (note) note.hidden = !variant?.quantity_price_breaks?.length;
      }

      _updateProductDataJSON(variant) {
        if (!variant) return;
        this._product = {
          ...this._product,
          selected_variant: variant,
          selected_or_first_available_variant: variant,
          current_variant: variant,
        };
        this.setAttribute("data-product-current-variant", String(variant.id));
        const script = this.querySelector("[data-product]");
        if (script) {
          script.textContent = JSON.stringify(this._product);
        }
      }

      _resetProductFormState() {
        const status = this.querySelector("[data-product-status]");
        if (status) status.textContent = "";
        this.querySelector("[data-add-to-cart]")?.removeAttribute("data-added");
        this.querySelectorAll("ai-product-form, [data-product-form]").forEach((form) => {
          form.handleErrorMessage?.();
          form.querySelector("[data-product-form-error]")?.setAttribute("hidden", "");
          form.querySelector("[data-recipient-email-error]")?.setAttribute("hidden", "");
        });
      }

      _hideUnavailableBusinessSurfaces(hidden = true) {
        [
          '[data-product-block="price"]',
          "[data-inventory-content]",
          "[data-product-sku]",
          "[data-product-price-per-item]",
          "[data-product-volume-pricing]",
          "[data-product-volume-note]",
          "ai-quantity-input[data-quantity-rules]",
        ].forEach((selector) => {
          this.querySelectorAll(selector).forEach((element) => {
            element.hidden = hidden;
          });
        });
      }

      _updateStickyCartInfo(variant) {
        if (!variant) return;
        const priceText = this.querySelector("[data-product-price]")?.textContent?.trim() || "";
        const imageSrc = this._getVariantImageSrc(variant);
        const disabled = !variant.available || this._isQuantityRuleSoldOut(variant);

        this.querySelectorAll("[data-sticky-cart], .product-sticky-cart").forEach((sticky) => {
          sticky.setAttribute("data-variant-id", String(variant.id));
          const price = sticky.querySelector("[data-sticky-cart-price]");
          if (price && priceText) price.textContent = priceText;

          const image = sticky.querySelector("[data-sticky-cart-image]");
          if (image && imageSrc) {
            image.setAttribute("src", imageSrc);
            image.setAttribute("alt", this._product?.title || image.getAttribute("alt") || "");
          }

          sticky.querySelectorAll("[data-sticky-cart-add], [data-sticky-cart-open].sticky-cart-btn--add").forEach((btn) => {
            btn.disabled = disabled;
            if (disabled) {
              btn.textContent = window.variantStrings?.soldOut || btn.textContent;
            } else {
              btn.textContent = window.variantStrings?.addToCart || btn.textContent;
            }
          });

          const paymentSlot = sticky.querySelector("[data-sticky-cart-payment-slot]");
          if (paymentSlot) paymentSlot.style.display = disabled ? "none" : "";
        });
      }

      async _calcProductPrice(variant) {
        if (!variant || !this._product?.id || !Array.isArray(this._product.discountList) || this._product.discountList.length === 0) {
          return;
        }

        try {
          const response = await fetch(`${window.genstore?.routes?.root || ""}/product-calc-price`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              productList: [
                {
                  productId: this._product.id,
                  variantId: variant.id,
                  quantity: Number(this.querySelector("[data-product-quantity-input]")?.value || 1),
                  salePrice: variant.price,
                  collectionIdList: (this._product.collections || []).map((collection) => collection.id),
                },
              ],
              discountList: this._product.discountList,
            }),
          });
          if (!response.ok) return;
          if (this._currentVariant?.id !== variant.id) return;
          const result = await response.json();
          this._applyCalculatedDiscountPrice(result, variant);
        } catch {
          // Promotion pricing is additive; the base variant price remains valid if calculation fails.
        }
      }

      _applyCalculatedDiscountPrice(result, variant) {
        const data = result?.data;
        if (String(result?.errcode ?? "0") !== "0" || !data || Number(data.totalDiscountValue || 0) <= 0) return;

        const totalPrice = Number(data.totalPrice || 0);
        const discount = Number(data.totalDiscountValue || 0);
        const discountedPrice = Math.max(0, totalPrice - discount);
        const symbol = this._product?.symbol || this.querySelector("[data-product-price]")?.textContent?.trim()?.match(/^[^\d\s]+/)?.[0] || "";
        const priceText = `${symbol}${symbol ? " " : ""}${discountedPrice.toFixed(2)}`;
        const compareText = `${symbol}${symbol ? " " : ""}${totalPrice.toFixed(2)}`;

        const priceEl = this.querySelector("[data-product-price]");
        const compareEl = this.querySelector("[data-product-compare-price]");
        const priceWrapper = this.querySelector('[data-product-block="price"]');
        if (priceEl) priceEl.textContent = priceText;
        if (compareEl) {
          compareEl.textContent = compareText;
          compareEl.hidden = false;
        }
        priceWrapper?.classList.add("price--on-sale");
        this._updateStickyCartInfo(variant);
      }

      _syncMediaModalAndXR(variant) {
        const mediaId = this._getVariantMediaId(variant);
        if (mediaId != null) {
          this.querySelectorAll("product-modal [data-media-id]").forEach((item) => {
            item.classList.toggle("product-media-modal__item--active", String(item.dataset.mediaId) === String(mediaId));
          });
        }
        window.ProductModel?.loadGenstoreXR?.();
      }

      _updateUrl(variant) {
        if (!variant?.id) return;
        const baseUrl = new URL(window.location.href, window.location.origin);
        baseUrl.searchParams.set("variant", variant.id);

        const shareUrl = `${window.location.origin}${baseUrl.pathname}${baseUrl.search}`;

        this.querySelectorAll("[data-share-button]").forEach((share) => {
          const shareBase = share.getAttribute("data-share-base-url") || `${window.location.origin}${baseUrl.pathname}`;
          share.setAttribute("href", `${shareBase}?variant=${variant.id}`);
        });

        this.querySelector("share-button")?.updateUrl?.(shareUrl);

        if (this.dataset.updateUrl === "false") return;
        window.history?.replaceState?.({}, "", `${baseUrl.pathname}${baseUrl.search}`);
      }

      _publishVariantChange(variant, html = null) {
        window.AIThemeEvents?.publishVariantChange?.({
          data: { sectionId: this.dataset.section, html, variant },
        });
        this.dispatchEvent(
          new CustomEvent("product:variant-change", {
            bubbles: true,
            detail: { sectionId: this.dataset.section, html, variant },
          }),
        );
      }

      _announceVariant(variant) {
        const status = this.querySelector("[data-product-status]");
        if (!status) return;
        status.textContent = variant?.available ? this._getAddText() : this._getSoldOutText();
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        return fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((htmlText) => callback(new DOMParser().parseFromString(htmlText, "text/html")))
          .then(() => {
            if (targetId) document.getElementById(targetId)?.focus();
          })
          .catch((error) => {
            if (error.name !== "AbortError") throw error;
          });
      }

      _updateMediaModalFromHtml(html) {
        const current = this.querySelector("product-modal .product-media-modal__content");
        const next = html.querySelector("product-modal .product-media-modal__content");
        if (current && next) current.innerHTML = next.innerHTML;
        this._syncMediaModalAndXR(this._currentVariant);
      }

      _setUnavailable() {
        this._currentVariant = null;
        this._updateVariantInputs(null);
        this._hideUnavailableBusinessSurfaces(true);
        this._updatePurchaseControls(null);
        this._showFormError(this._getUnavailableText());
      }

      _showFormError(message) {
        const wrapper = this.querySelector("[data-product-form-error]");
        if (!wrapper) return;
        const messageEl = wrapper.querySelector("[data-product-form-error-message]");
        if (messageEl) messageEl.textContent = message || "";
        wrapper.hidden = !message;
      }

      _onStickyClick(event) {
        const open = event.target.closest("[data-sticky-cart-open]");
        const close = event.target.closest("[data-sticky-cart-close]");
        const overlay = event.target.matches("[data-sticky-cart-overlay]");
        const add = event.target.closest("[data-sticky-cart-add]");
        const stickyOverlay = this.querySelector("[data-sticky-cart-overlay]");

        if (open && stickyOverlay) {
          stickyOverlay.hidden = false;
          requestAnimationFrame(() => stickyOverlay.classList.add("is-open"));
          document.body.style.overflow = "hidden";
          this._movePaymentButtonToModal();
        }
        if ((close || overlay) && stickyOverlay) {
          stickyOverlay.classList.remove("is-open");
          document.body.style.overflow = "";
          this._restorePaymentButton();
          setTimeout(() => { stickyOverlay.hidden = true; }, 300);
        }
        if (add) {
          this._stickyAddToCart();
        }
      }

      async _stickyAddToCart() {
        const stickyEl = this.querySelector("[data-sticky-cart]");
        const variantId = stickyEl?.getAttribute("data-variant-id");
        const quantityInput = stickyEl?.querySelector("ai-quantity-input");
        const quantity = parseInt(quantityInput?.getAttribute("value") || quantityInput?.value || "1", 10) || 1;
        if (!variantId) return;

        try {
          const response = await fetch(`${window.genstore?.routes?.root || ""}/cart/add.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity }] }),
          });
          if (response.ok) {
            this._closeStickyOverlay();
            const cartUrl = window.routes?.cart_url || "/cart";
            window.location = cartUrl + (window.location.search || "");
          }
        } catch (e) {
          console.error("Sticky cart add error:", e);
        }
      }

      _movePaymentButtonToModal() {
        const paymentButton = this.querySelector(".product-buy-now");
        const slot = this.querySelector("[data-sticky-cart-payment-slot]");
        if (!paymentButton || !slot) return;
        this._paymentButtonOriginalParent = paymentButton.parentElement;
        this._paymentButtonNextSibling = paymentButton.nextElementSibling;
        slot.appendChild(paymentButton);
        const variant = this._currentVariant;
        const disabled = !variant?.available || this._isQuantityRuleSoldOut(variant);
        slot.style.display = disabled ? "none" : "";
      }

      _restorePaymentButton() {
        const paymentButton = this.querySelector("[data-sticky-cart-payment-slot] .product-buy-now");
        if (!paymentButton || !this._paymentButtonOriginalParent) return;
        if (this._paymentButtonNextSibling) {
          this._paymentButtonOriginalParent.insertBefore(paymentButton, this._paymentButtonNextSibling);
        } else {
          this._paymentButtonOriginalParent.appendChild(paymentButton);
        }
        this._paymentButtonOriginalParent = null;
        this._paymentButtonNextSibling = null;
      }

      _closeStickyOverlay() {
        const stickyOverlay = this.querySelector("[data-sticky-cart-overlay]");
        if (!stickyOverlay) return;
        stickyOverlay.classList.remove("is-open");
        document.body.style.overflow = "";
        setTimeout(() => { stickyOverlay.hidden = true; }, 300);
      }

      _imageSrc(image) {
        if (!image) return null;
        if (typeof image === "string") return image;
        if (typeof image.src === "string") return image.src;
        if (typeof image.src?.src === "string") return image.src.src;
        if (typeof image.url === "string") return image.url;
        if (typeof image.preview_image?.src === "string") return image.preview_image.src;
        if (typeof image.preview_image?.src?.src === "string") return image.preview_image.src.src;
        if (typeof image.preview_image?.url === "string") return image.preview_image.url;
        return null;
      }

      _getVariantImageSrc(variant) {
        if (!variant) return null;
        return this._imageSrc(variant.featured_image) ||
          this._imageSrc(variant.image) ||
          this._imageSrc(variant.featured_media) ||
          this._imageSrc(variant.featured_media?.preview_image) ||
          variant.featured_image_url ||
          variant.image_url ||
          null;
      }

      _getVariantMediaId(variant) {
        if (!variant) return null;
        if (variant.featured_media?.id) return variant.featured_media.id;
        const imageSrc = this._getVariantImageSrc(variant);
        if (!imageSrc) return null;
        const filename = this._fileName(imageSrc);
        const matched = this._media.find((media) => {
          const mediaSrc = this._imageSrc(media) || this._imageSrc(media.preview_image) || "";
          return this._fileName(mediaSrc) === filename;
        });
        return matched?.id || null;
      }

      _fileName(src) {
        return String(src).split("?")[0].split("/").pop();
      }

      _readJSON(selector, fallback) {
        const script = this.querySelector(selector);
        if (!script) return fallback;
        try {
          return JSON.parse(script.textContent);
        } catch {
          return fallback;
        }
      }

      _getAddText() {
        return this.querySelector("[data-add-text]")?.getAttribute("data-add-text") ||
          window.variantStrings?.addToCart ||
          "Add to cart";
      }

      _getSoldOutText() {
        return this.querySelector("[data-sold-out-text]")?.getAttribute("data-sold-out-text") ||
          window.variantStrings?.soldOut ||
          "Sold out";
      }

      _getUnavailableText() {
        return this.querySelector("[data-unavailable-text]")?.getAttribute("data-unavailable-text") ||
          window.variantStrings?.unavailable ||
          "Unavailable";
      }

      _replaceMoneyAmount(input, newValue) {
        if (!input || newValue == null) return input || "";
        const amount = Number(newValue) / 100;
        const symbolFirstPattern = /^([^\d\s]+)\s?([\d,]*\.?\d+)(.*)$/;
        const numberFirstPattern = /^([\d,]*\.?\d+)\s?([^\d,.\s]+.*)$/;
        const symbolFirstMatch = input.match(symbolFirstPattern);
        const numberFirstMatch = input.match(numberFirstPattern);
        let oldNumber;
        let isSymbolFirst;

        if (symbolFirstMatch && symbolFirstMatch[2]) {
          oldNumber = symbolFirstMatch[2];
          isSymbolFirst = true;
        } else if (numberFirstMatch && numberFirstMatch[1]) {
          oldNumber = numberFirstMatch[1];
          isSymbolFirst = false;
        } else {
          return amount.toFixed(2);
        }

        const useThousandSeparator = oldNumber.includes(",");
        const decimals = oldNumber.includes(".") ? oldNumber.split(".")[1].length : 2;
        const formatted = useThousandSeparator
          ? amount.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
          : amount.toFixed(decimals);

        if (isSymbolFirst) {
          const [fullMatch, symbol, , suffix] = symbolFirstMatch;
          const hasSpace = fullMatch.charAt(symbol.length) === " ";
          return `${symbol}${hasSpace ? " " : ""}${formatted}${suffix || ""}`;
        }

        const [fullMatch, number, suffix] = numberFirstMatch;
        const hasSpace = fullMatch.charAt(number.length) === " ";
        return `${formatted}${hasSpace ? " " : ""}${suffix.trim()}`;
      }
    },
  );
}
